// 배송비 정책 공유 폼 (2026-08-24 KST 추가)
// 기본 배송 설정 화면과 상품 편집의 "배송 설정" 카드가 함께 사용한다.
// 선택한 배송비 유형/추가 배송비 방식에 따라 입력 항목이 조건부로 렌더된다.
import { Box, Button, Input, Paragraph, RadioGroup, Text } from "@saleor/macaw-ui-next";

import {
  type ShippingBracketForm,
  type ShippingFeeType,
  type ShippingPolicyFormData,
  type SurchargeMode,
} from "../../types";

// 상품 목록의 배송 태그도 같은 이름을 쓴다 (2026-10-02 KST export)
export const FEE_TYPE_OPTIONS: Array<{ value: ShippingFeeType; label: string }> = [
  { value: "free", label: "무료배송" },
  { value: "conditional_free", label: "조건부 무료배송" },
  { value: "fixed", label: "고정 배송비" },
  { value: "per_quantity", label: "수량별 배송비" },
  { value: "per_amount", label: "금액별 배송비" },
  { value: "repeat_quantity", label: "반복 수량 배송비" },
];

interface MoneyInputProps {
  value: string;
  onChange: (value: string) => void;
  suffix?: string;
  width?: string;
  testId?: string;
}

// 숫자만 남기고 상위로 올리는 소형 금액/수량 입력 ("원"/"개" suffix 포함)
// 화면에는 천 단위 콤마를 붙여 보이고, 값은 숫자만 저장한다 (2026-10-16 KST 수정)
const MoneyInput = ({
  value,
  onChange,
  suffix = "원",
  width = "120px",
  testId,
}: MoneyInputProps) => (
  <Box display="flex" alignItems="center" gap={1}>
    <Box __width={width}>
      <Input
        size="small"
        value={value ? Number(value).toLocaleString("ko-KR") : value}
        onChange={event => onChange(event.target.value.replace(/[^\d]/g, ""))}
        data-test-id={testId}
      />
    </Box>
    <Text size={2}>{suffix}</Text>
  </Box>
);

interface BracketRowsProps {
  brackets: ShippingBracketForm[];
  limitSuffix: string;
  limitWidth?: string;
  onChange: (brackets: ShippingBracketForm[]) => void;
}

// 수량별/금액별 "조건 추가 +" 반복 행
const BracketRows = ({
  brackets,
  limitSuffix,
  limitWidth = "120px",
  onChange,
}: BracketRowsProps) => {
  const updateRow = (index: number, patch: Partial<ShippingBracketForm>) =>
    onChange(brackets.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {brackets.map((row, index) => (
        <Box key={index} display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <MoneyInput
            value={row.limit}
            onChange={limit => updateRow(index, { limit })}
            suffix={`${limitSuffix}까지 구매 시`}
            width={limitWidth}
            testId={`bracket-limit-${index}`}
          />
          <MoneyInput
            value={row.fee}
            onChange={fee => updateRow(index, { fee })}
            suffix="원 부과"
            testId={`bracket-fee-${index}`}
          />
          {brackets.length > 1 && (
            // 삭제 버튼은 빨강으로 통일 (2026-09-30 KST 수정)
            <Button
              variant="error"
              size="small"
              onClick={() => onChange(brackets.filter((_, i) => i !== index))}
            >
              삭제
            </Button>
          )}
        </Box>
      ))}
      <Box display="flex" alignItems="center" gap={3}>
        <Button
          variant="secondary"
          size="small"
          onClick={() => onChange([...brackets, { limit: "", fee: "" }])}
        >
          조건 추가 +
        </Button>
        <Text color="default2" size={2}>
          마지막 조건을 초과하면 마지막 조건의 배송비가 적용됩니다.
        </Text>
      </Box>
    </Box>
  );
};

interface ShippingPolicyFormProps {
  value: ShippingPolicyFormData;
  onChange: (next: ShippingPolicyFormData) => void;
}

// 배송비 유형과 추가 배송비를 따로 쓸 수 있게 나눴다. 상품 등록/수정 화면은 둘을 각각
// 공통/개별로 고른다 (2026-10-12 KST 수정)
export const ShippingPolicyForm = ({ value, onChange }: ShippingPolicyFormProps) => (
  <Box display="flex" flexDirection="column" gap={6}>
    <Box display="flex" flexDirection="column" gap={3}>
      <Text fontWeight="bold">배송비 유형</Text>
      <FeeTypeFields value={value} onChange={onChange} />
    </Box>
    <Box display="flex" flexDirection="column" gap={3}>
      <Text fontWeight="bold">추가 배송비</Text>
      <SurchargeFields value={value} onChange={onChange} />
    </Box>
  </Box>
);

export const FeeTypeFields = ({ value, onChange }: ShippingPolicyFormProps) => {
  const patch = (fields: Partial<ShippingPolicyFormData>) => onChange({ ...value, ...fields });

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <RadioGroup
        value={value.feeType}
        onValueChange={feeType => patch({ feeType: feeType as ShippingFeeType })}
        display="flex"
        flexWrap="wrap"
        gap={4}
      >
        {FEE_TYPE_OPTIONS.map(option => (
          <RadioGroup.Item
            key={option.value}
            id={`fee-type-${option.value}`}
            value={option.value}
            data-test-id={`fee-type-${option.value}`}
          >
            <Paragraph fontSize={3} margin={0}>
              {option.label}
            </Paragraph>
          </RadioGroup.Item>
        ))}
      </RadioGroup>

      {value.feeType === "free" && (
        <Text color="default2" size={2}>
          구매 금액과 상관없이 배송비를 부과하지 않습니다.
        </Text>
      )}

      {value.feeType === "conditional_free" && (
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Text size={2}>기본 배송비</Text>
          <MoneyInput
            value={value.baseFee}
            onChange={baseFee => patch({ baseFee })}
            suffix="원 /"
            testId="conditional-base-fee"
          />
          <MoneyInput
            value={value.freeThreshold}
            onChange={freeThreshold => patch({ freeThreshold })}
            suffix="원 이상일 때 배송비 무료"
            testId="conditional-threshold"
          />
        </Box>
      )}

      {value.feeType === "fixed" && (
        <Box display="flex" alignItems="center" gap={2}>
          <Text size={2}>구매 금액에 상관없이 배송비</Text>
          <MoneyInput
            value={value.baseFee}
            onChange={baseFee => patch({ baseFee })}
            suffix="원을 고정으로 부과"
            testId="fixed-base-fee"
          />
        </Box>
      )}

      {value.feeType === "per_quantity" && (
        <BracketRows
          brackets={value.brackets}
          limitSuffix="개"
          limitWidth="90px"
          onChange={brackets => patch({ brackets })}
        />
      )}

      {value.feeType === "per_amount" && (
        <BracketRows
          brackets={value.brackets}
          limitSuffix="원"
          onChange={brackets => patch({ brackets })}
        />
      )}

      {value.feeType === "repeat_quantity" && (
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Text size={2}>기본 배송비</Text>
          <MoneyInput
            value={value.baseFee}
            onChange={baseFee => patch({ baseFee })}
            suffix="원 / 상품"
            testId="repeat-base-fee"
          />
          <MoneyInput
            value={value.repeatUnit}
            onChange={repeatUnit => patch({ repeatUnit })}
            suffix="개 구매 시마다 반복 부과"
            width="90px"
            testId="repeat-unit"
          />
        </Box>
      )}
    </Box>
  );
};

export const SurchargeFields = ({ value, onChange }: ShippingPolicyFormProps) => {
  const patch = (fields: Partial<ShippingPolicyFormData>) => onChange({ ...value, ...fields });

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <RadioGroup
        value={value.surchargeMode}
        onValueChange={mode => patch({ surchargeMode: mode as SurchargeMode })}
        display="flex"
        flexWrap="wrap"
        gap={4}
      >
        <RadioGroup.Item id="surcharge-zone" value="zone" data-test-id="surcharge-zone">
          <Paragraph fontSize={3} margin={0}>
            배송권역 방식
          </Paragraph>
        </RadioGroup.Item>
        {/* 지역별 배송비 방식 (2026-08-24 KST 활성화) */}
        <RadioGroup.Item id="surcharge-region" value="region" data-test-id="surcharge-region">
          <Paragraph fontSize={3} margin={0}>
            지역별 배송비 방식
          </Paragraph>
        </RadioGroup.Item>
        <RadioGroup.Item id="surcharge-none" value="none" data-test-id="surcharge-none">
          <Paragraph fontSize={3} margin={0}>
            사용안함
          </Paragraph>
        </RadioGroup.Item>
      </RadioGroup>

      {value.surchargeMode === "zone" && (
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Text size={2}>제주지역 추가 배송비</Text>
            <MoneyInput
              value={value.jejuFee}
              onChange={jejuFee => patch({ jejuFee })}
              suffix="원 /"
              testId="jeju-fee"
            />
            <Text size={2}>도서산간지역 추가 배송비</Text>
            <MoneyInput
              value={value.islandFee}
              onChange={islandFee => patch({ islandFee })}
              suffix="원"
              testId="island-fee"
            />
          </Box>
          <Text color="default2" size={2}>
            배송지의 우편번호가 제주/도서산간 지역과 일치하면 추가 배송비가 부과됩니다.
          </Text>
        </Box>
      )}

      {value.surchargeMode === "region" && (
        <Text color="default2" size={2}>
          &quot;도서/산간 지역 배송비&quot; 화면에 등록한 우편번호별 금액이 부과됩니다. 표에 없는
          우편번호는 추가 배송비가 없습니다.
        </Text>
      )}
    </Box>
  );
};
